import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex } from "antd";
import SbCollapse from "uiCore/SbCollapse/SbCollapse";

import useAuthStore from "Store/useAuthStore";
import { fetchOrgStructure, organizationStructure } from "Api/apiService";

import { countFunctions, countSubFunctions, cloneStructure } from "./helper";
import StatsBar from "./StatsBar";
import EntityPanelHeader from "./EntityPanelHeader";
import EntityPanelBody from "./EntityPanelBody";
import AddEntityForm from "./AddEntityForm";

import {
  BodyCardStyled,
} from "./LegalEntityManagement.styles";
import { BUTTON_TYPE, CaretRightOutlined, PlusOutlined, ReloadOutlined, SbButton, SbDivider, SbEmpty, SbNotification, SbSpin } from "uiCore";
import PageHeader from "Components/PageHeader";
import { PageContainerStyled, PageBodyStyled } from "styles/common.styles";

const LegalEntityManagement = () => {
  const navigate = useNavigate();

  const storeStructure = useAuthStore((s) => s.orgMetaData?.organization_structure);
  const setOrgMetaData = useAuthStore((s) => s.setOrgMetaData);

  const [structure, setStructure] = useState(() => cloneStructure(storeStructure || []));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddEntity, setShowAddEntity] = useState(false);
  const [activeKeys, setActiveKeys] = useState([]);

  // Snapshot of the last loaded/saved structure. handleSave compares against it
  const savedSnapshotRef = useRef(null);

  const loadStructure = useCallback(
    async ({ notify = false } = {}) => {
      try {
        const data = await fetchOrgStructure();
        const list =
          data?.organization_structure || data?.data?.organization_structure || [];
        const cloned = cloneStructure(list);
        savedSnapshotRef.current = JSON.stringify(cloned);
        setStructure(cloned);
        setActiveKeys([]);
        setShowAddEntity(false);
        setOrgMetaData({
          ...(useAuthStore.getState().orgMetaData || {}),
          organization_structure: cloned,
        });
        if (notify) {
          SbNotification.success("Refreshed", "Organization structure reloaded.");
        }
      } catch (error) {
        SbNotification.error(
          "Failed to load",
          "Could not fetch organization structure.",
        );
      } finally {
        setLoading(false);
      }
    },
    [setOrgMetaData],
  );

  useEffect(() => {
    loadStructure();
  }, [loadStructure]);

  const updateEntity = (entityIdx, mutator) => {
    setStructure((prev) => {
      const next = cloneStructure(prev);
      mutator(next[entityIdx]);
      return next;
    });
  };

  const addEntity = (entity) => {
    setStructure((prev) => [...prev, entity]);
    setActiveKeys((prev) => [...prev, entity.legal_entity]);
    setShowAddEntity(false);
  };

  const addBu = (entityIdx, name) =>
    updateEntity(entityIdx, (entity) => {
      entity.business_units.push({
        business_unit: name,
        business_unit_description: "",
        functions: [],
      });
    });

  const addFunction = (entityIdx, buIdx, name) =>
    updateEntity(entityIdx, (entity) => {
      entity.business_units[buIdx].functions.push({
        function: name,
        function_description: "",
        sub_functions: [],
      });
    });

  const addSubFunction = (entityIdx, buIdx, fnIdx, name) =>
    updateEntity(entityIdx, (entity) => {
      entity.business_units[buIdx].functions[fnIdx].sub_functions.push({
        description: "",
        sub_function: name,
      });
    });

  const handleSave = async () => {
    // Skip the API call when nothing changed vs the last loaded/saved state.
    if (JSON.stringify(structure) === savedSnapshotRef.current) {
      SbNotification.info("No changes", "There is nothing to save.");
      return;
    }
    setSaving(true);
    try {
      await organizationStructure({ organization_structure: structure });
      SbNotification.success("Saved", "Organization structure updated.");
      await loadStructure();
    } catch (error) {
      SbNotification.error(
        "Save failed",
        "Could not update organization structure.",
      );
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const entities = structure.length;
    const bus = structure.reduce((a, e) => a + (e.business_units?.length || 0), 0);
    const fns = structure.reduce((a, e) => a + countFunctions(e), 0);
    const subs = structure.reduce((a, e) => a + countSubFunctions(e), 0);
    return { entities, bus, fns, subs };
  }, [structure]);

  const existingEntityNames = useMemo(
    () => structure.map((e) => e.legal_entity).filter(Boolean),
    [structure],
  );

  // Deduped option suggestions for the inline add inputs — collected from every
  // entity in the current structure.
  const { buOptions, fnOptions, subFnOptions } = useMemo(() => {
    const bu = new Set();
    const fn = new Set();
    const sub = new Set();
    structure.forEach((entity) => {
      (entity.business_units || []).forEach((b) => {
        if (b.business_unit) bu.add(b.business_unit);
        (b.functions || []).forEach((f) => {
          if (f.function) fn.add(f.function);
          (f.sub_functions || []).forEach((s) => {
            if (s.sub_function) sub.add(s.sub_function);
          });
        });
      });
    });
    return { buOptions: [...bu], fnOptions: [...fn], subFnOptions: [...sub] };
  }, [structure]);

  const collapseItems = structure.map((entity, entityIdx) => ({
    key: entity.legal_entity || `entity-${entityIdx}`,
    label: <EntityPanelHeader entity={entity} index={entityIdx} />,
    children: (
      <EntityPanelBody
        entity={entity}
        buOptions={buOptions}
        fnOptions={fnOptions}
        subFnOptions={subFnOptions}
        onAddBu={(name) => addBu(entityIdx, name)}
        onAddFunction={(buIdx, name) => addFunction(entityIdx, buIdx, name)}
        onAddSubFunction={(buIdx, fnIdx, name) =>
          addSubFunction(entityIdx, buIdx, fnIdx, name)
        }
      />
    ),
  }));

  return (
    <PageContainerStyled>
      <PageHeader
        title="Legal Entity"
        onBack={() => navigate("/admin-setup")}
        backLabel="Back to Admin Setup"
        actions={
          <SbButton
            buttonType={BUTTON_TYPE.SECONDARY}
            icon={<ReloadOutlined />}
            onClick={() => loadStructure({ notify: true })}
          >
            Refresh
          </SbButton>
        }
      />

      <PageBodyStyled>
      <SbDivider />

      <StatsBar stats={stats} />

      <SbDivider />

      <BodyCardStyled>
        <SbSpin spinning={loading}>
          {structure.length === 0 ? (
            <SbEmpty description="No legal entities yet. Add one below." />
          ) : (
            <SbCollapse
              activeKey={activeKeys}
              onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
              items={collapseItems}
            />
          )}

          {showAddEntity ? (
            <AddEntityForm
              existingNames={existingEntityNames}
              onAdd={addEntity}
              onCancel={() => setShowAddEntity(false)}
            />
          ) : (
            <SbButton
              type="dashed"
              block
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setShowAddEntity(true)}
            >
              Add legal entity
            </SbButton>
          )}
        </SbSpin>
      </BodyCardStyled>

      <Flex justify="flex-end" gap={10}>
        <SbButton
          buttonType={BUTTON_TYPE.SECONDARY}
          onClick={() => navigate("/admin-setup")}
        >
          Cancel
        </SbButton>
        <SbButton
          buttonType={BUTTON_TYPE.PRIMARY}
          onClick={handleSave}
          loading={saving}
          disabled={loading || saving}
        >
          Save Changes
        </SbButton>
      </Flex>
      </PageBodyStyled>
    </PageContainerStyled>
  );
};

export default LegalEntityManagement;
